import {
    useContext,
    useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { DashboardHeader } from './components/DashboardHeader';
import { OrderChart } from './components/OrderChart';
import {
    type Range,
    RANGE_COMPARE_LABEL,
    RANGE_DAYS,
} from './components/range';
import { RecentOrdersCard } from './components/RecentOrdersCard';
import { RelatedPartiesCard } from './components/RelatedPartiesCard';
import { SchemesOffersCard } from './components/SchemesOffers';
import { StatCard } from './components/StatCards';
import { TopProductsCard } from './components/TopProductsCard';
import { fetchDashboard } from './api';
import type {
    DashboardData,
    DashboardOverviewMetric,
} from './types';

import { Spinner } from '@/components/ui/spinner';
import AuthContext from '@/contexts/auth';
import { formatMoney } from '@/lib/shared-utils';

// trend_percent is null when the backend has no prior-period baseline to
// compare against (e.g. a metric with no history yet) — omit the delta then.
function statProps(metric: DashboardOverviewMetric, compareNote: string) {
    if (metric.trend_percent === null) return {};
    return {
        delta: `${Math.abs(metric.trend_percent).toFixed(1)}%`,
        deltaNote: compareNote,
        positive: metric.trend_percent >= 0,
    };
}

function Dashboard() {
    const navigate = useNavigate();
    const { state } = useContext(AuthContext);
    const name = state?.businessName || state?.name || 'there';

    const [range, setRange] = useState<Range>('14D');
    const days = RANGE_DAYS[range];
    const compareNote = RANGE_COMPARE_LABEL[range];

    const { data: res, isLoading } = useQuery({
        queryKey: ['dashboard', days],
        queryFn: () => fetchDashboard(days),
    });
    const dash = res?.ok ? (res.data as DashboardData) : undefined;

    const salesChartData = (dash?.order_chart?.labels ?? []).map((date, index) => ({
        date,
        orders: dash?.order_chart?.series?.orders?.[index] ?? 0,
    }));

    const recentOrders = (dash?.recent_orders ?? []).map((order) => ({
        id: order.order_id,
        order_uuid: order.order_uuid,
        order_email: '',
        customer_name: order.customer_name,
        amount: formatMoney(order.amount),
        status: order.status,
        status_display: order.status_display,
        created_at: order.date,
    }));

    const topProducts = (dash?.top_selling_products ?? []).map((product) => ({
        id: product.product_id,
        title: product.product_name,
        slug: product.product_slug,
        total_sold: product.quantity,
        price: formatMoney(product.amount),
    }));

    const goToOrders = () => navigate('/orders');
    const goToNewOrder = () => navigate('/orders/create');
    const goToParties = () => navigate('/related-parties');

    return (
        <div className="space-y-5">
            <DashboardHeader
                name={name}
                range={range}
                onRangeChange={setRange}
                onNewOrder={goToNewOrder}
            />

            {isLoading && (
                <div className="flex justify-center py-16">
                    <Spinner className="h-6 w-6 text-muted-foreground" />
                </div>
            )}

            {!isLoading && dash && (
                <>
                    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                        <StatCard
                            title="Incoming Orders"
                            value={dash.overview.incoming_orders.value}
                            {...statProps(dash.overview.incoming_orders, compareNote)}
                        />
                        <StatCard
                            title="Outgoing Orders"
                            value={dash.overview.outgoing_orders.value}
                            {...statProps(dash.overview.outgoing_orders, compareNote)}
                        />
                        <StatCard
                            title="Products"
                            value={dash.overview.products.value}
                            {...statProps(dash.overview.products, compareNote)}
                        />
                        <StatCard
                            title="Customers"
                            value={dash.overview.customers.value}
                            {...statProps(dash.overview.customers, compareNote)}
                        />
                    </div>

                    <div className="grid gap-4 lg:grid-cols-5">
                        <RecentOrdersCard orders={recentOrders} onSeeAll={goToOrders} />
                        <RelatedPartiesCard parties={dash.related_parties} onManage={goToParties} />
                    </div>

                    <div className="grid gap-4 lg:grid-cols-5">
                        <OrderChart salesChartData={salesChartData} days={days} />
                        <SchemesOffersCard />
                    </div>

                    <TopProductsCard products={topProducts} />
                </>
            )}
        </div>
    );
}

Dashboard.displayName = 'Dashboard';
export default Dashboard;
